import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  CalendarDays,
  CalendarPlus,
  MapPin,
  Clock,
  Users,
  Link as LinkIcon,
  Loader2,
  CheckCircle,
  XCircle,
  HelpCircle,
  Shirt,
  DollarSign,
  Image,
  Video,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../services/api';
import { getBusinessTimezone, formatDateDisplay, formatTimeDisplay } from '../../utils/timezone';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

// Strip HTML → plain text for card excerpts. The description field
// comes from the rich-text editor as HTML markup; when rendered inside
// a <p>{description}</p> the tags leak as literal text. This helper
// gives a clean single-line excerpt while the details modal still
// renders the full HTML via DOMPurify.
const htmlToPlainText = (html) => {
  if (!html) return '';
  try {
    const stripped = String(html)
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    return stripped;
  } catch {
    return String(html);
  }
};

// Cards are summaries; detailed rich text remains available in the
// detail dialog. Enforce the bound in JavaScript so builds without the
// line-clamp utility cannot expand every event card.
const eventDescriptionExcerpt = (html, maxLength = 240) => {
  const text = htmlToPlainText(html);
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
};

// -------- Add-to-calendar helpers --------
// Format a JS Date as UTC ICS/Google-URL basic-format string
// (e.g. 20260315T140000Z).
const toIcsUtc = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear() +
    p(d.getUTCMonth() + 1) +
    p(d.getUTCDate()) +
    'T' +
    p(d.getUTCHours()) +
    p(d.getUTCMinutes()) +
    p(d.getUTCSeconds()) +
    'Z'
  );
};

const buildGoogleCalendarUrl = (event) => {
  const start = new Date(event.start_datetime);
  const end = new Date(event.end_datetime);
  const details = htmlToPlainText(event.description || '');
  const location = event.is_virtual
    ? (event.virtual_meeting_link || 'Virtual')
    : (event.location || '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Event',
    dates: `${toIcsUtc(start)}/${toIcsUtc(end)}`,
    details,
    location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildIcsBlob = (event) => {
  const start = new Date(event.start_datetime);
  const end = new Date(event.end_datetime);
  const uid = `${event.id}@dascricket`;
  const now = new Date();
  const escape = (s) =>
    String(s || '')
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  const location = event.is_virtual
    ? (event.virtual_meeting_link || 'Virtual')
    : (event.location || '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//DAS Cricket//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toIcsUtc(now)}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escape(event.title || 'Event')}`,
    `DESCRIPTION:${escape(htmlToPlainText(event.description || ''))}`,
    `LOCATION:${escape(location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
};

const downloadIcs = (event) => {
  const blob = buildIcsBlob(event);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.title || 'event').replace(/[^a-z0-9-_]+/gi, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const ParentEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  
  // RSVP Dialog
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [rsvpData, setRsvpData] = useState({
    status: '',
    num_guests: 0,
    num_children: 0,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Event details modal
  const [detailEvent, setDetailEvent] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const init = async () => {
      const tz = await getBusinessTimezone(api);
      setBusinessTimezone(tz);
      fetchEvents();
    };
    init();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events?include_past=true');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const openRsvpDialog = (event) => {
    setRsvpEvent(event);
    // Pre-fill with existing RSVP if any
    if (event.user_rsvp) {
      setRsvpData({
        status: event.user_rsvp.status || '',
        num_guests: event.user_rsvp.num_guests || 0,
        num_children: event.user_rsvp.num_children || 0,
        notes: event.user_rsvp.notes || ''
      });
    } else {
      setRsvpData({
        status: '',
        num_guests: 0,
        num_children: 0,
        notes: ''
      });
    }
  };

  const handleSubmitRsvp = async () => {
    if (!rsvpData.status) {
      toast.error('Please select your RSVP response');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/events/rsvp', {
        event_id: rsvpEvent.id,
        ...rsvpData
      });
      
      toast.success(rsvpEvent.user_rsvp ? 'RSVP updated!' : 'RSVP submitted!');
      setRsvpEvent(null);
      fetchEvents();
    } catch (err) {
      console.error('RSVP error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRsvp = async () => {
    if (!rsvpEvent?.user_rsvp?.id) return;
    
    setSubmitting(true);
    try {
      await api.delete(`/events/rsvp/${rsvpEvent.user_rsvp.id}`);
      toast.success('RSVP cancelled');
      setRsvpEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error('Failed to cancel RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    // Use business timezone for display
    return formatDateDisplay(isoString, businessTimezone);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    // Use business timezone for display
    return formatTimeDisplay(isoString, businessTimezone);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'yes':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Attending</Badge>;
      case 'no':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Not Attending</Badge>;
      case 'maybe':
        return <Badge className="bg-yellow-500"><HelpCircle className="w-3 h-3 mr-1" />Maybe</Badge>;
      default:
        return null;
    }
  };

  // Filter events
  const now = new Date();
  const upcomingEvents = events.filter(e => new Date(e.end_datetime) >= now);
  const pastEvents = events.filter(e => new Date(e.end_datetime) < now);
  const myRsvps = events.filter(e => e.user_rsvp);

  const filteredEvents = activeTab === 'upcoming' ? upcomingEvents 
    : activeTab === 'past' ? pastEvents 
    : myRsvps;

  if (loading) {
    return (
      <DashboardLayout title="Events">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Academy Events">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Academy Events</h1>
          <p className="text-slate-600">View upcoming events and manage your RSVPs</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              Upcoming ({upcomingEvents.length})
            </TabsTrigger>
            <TabsTrigger value="myrsvps" data-testid="tab-myrsvps">
              My RSVPs ({myRsvps.length})
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              Past ({pastEvents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filteredEvents.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <CalendarDays className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg font-medium">
                    {activeTab === 'upcoming' ? 'No upcoming events' 
                      : activeTab === 'myrsvps' ? "You haven't RSVP'd to any events" 
                      : 'No past events'}
                  </p>
                  <p className="text-sm mt-1">Check back later for new events</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onRsvp={() => openRsvpDialog(event)}
                    onOpenDetails={() => { setGalleryIndex(0); setDetailEvent(event); }}
                    formatDate={formatDate}
                    formatTime={formatTime}
                    getStatusBadge={getStatusBadge}
                    isPast={new Date(event.end_datetime) < now}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* RSVP Dialog */}
      <Dialog open={!!rsvpEvent} onOpenChange={(open) => !open && setRsvpEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{rsvpEvent?.user_rsvp ? 'Update RSVP' : 'RSVP to Event'}</DialogTitle>
            <DialogDescription>{rsvpEvent?.title}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* RSVP Status Selection */}
            <div>
              <Label className="text-base font-medium mb-3 block">Will you attend? *</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: 'yes', label: 'Yes', icon: CheckCircle, color: 'emerald' },
                  { value: 'maybe', label: 'Maybe', icon: HelpCircle, color: 'yellow' },
                  { value: 'no', label: 'No', icon: XCircle, color: 'red' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setRsvpData(prev => ({ ...prev, status: option.value }))}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      rsvpData.status === option.value
                        ? option.color === 'emerald' 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : option.color === 'yellow'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                          : 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`rsvp-${option.value}`}
                  >
                    <option.icon className="w-5 h-5" />
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Guests & Children */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Additional Guests</Label>
                <Input
                  type="number"
                  min="0"
                  value={rsvpData.num_guests}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, num_guests: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={rsvpData.num_children}
                  onChange={(e) => setRsvpData(prev => ({ ...prev, num_children: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={rsvpData.notes}
                onChange={(e) => setRsvpData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any dietary requirements or notes..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Capacity Warning */}
            {rsvpData.status === 'yes' && rsvpEvent?.max_capacity && (
              <div className="text-sm text-slate-500">
                <Users className="w-4 h-4 inline mr-1" />
                {rsvpEvent.rsvp_counts?.total_attending || 0} / {rsvpEvent.max_capacity} spots filled
                {rsvpEvent.enable_waitlist && rsvpEvent.rsvp_counts?.total_attending >= rsvpEvent.max_capacity && (
                  <span className="text-yellow-600 ml-2">(You&apos;ll be added to waitlist)</span>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {rsvpEvent?.user_rsvp && (
              <Button 
                variant="outline" 
                onClick={handleCancelRsvp} 
                disabled={submitting}
                className="text-red-600 hover:text-red-700"
              >
                Cancel RSVP
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setRsvpEvent(null)}>
                Close
              </Button>
              <Button onClick={handleSubmitRsvp} disabled={submitting} data-testid="submit-rsvp-btn">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  rsvpEvent?.user_rsvp ? 'Update RSVP' : 'Submit RSVP'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={!!detailEvent} onOpenChange={(open) => !open && setDetailEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-details-dialog">
          {detailEvent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {detailEvent.user_rsvp && getStatusBadge(detailEvent.user_rsvp.status)}
                  {detailEvent.is_virtual && (
                    <Badge variant="outline"><LinkIcon className="w-3 h-3 mr-1" />Virtual</Badge>
                  )}
                </div>
                <DialogTitle data-testid="event-details-title">{detailEvent.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-5 py-2">
                {/* Media gallery */}
                {(() => {
                  const imgs = (detailEvent.media || []).filter(m => m.type === 'image');
                  const vid = (detailEvent.media || []).find(m => m.type === 'video');
                  if (imgs.length === 0 && !vid) return null;
                  return (
                    <div className="space-y-2" data-testid="event-details-media">
                      {imgs.length > 0 && (
                        <div className="bg-slate-100 rounded-lg overflow-hidden">
                          <img
                            src={`${API_URL}/api/events/${detailEvent.id}/media/${imgs[Math.min(galleryIndex, imgs.length - 1)].id}`}
                            alt={detailEvent.title}
                            className="w-full max-h-96 object-contain"
                          />
                          {imgs.length > 1 && (
                            <div className="flex gap-2 p-2 overflow-x-auto">
                              {imgs.map((img, idx) => (
                                <button
                                  key={img.id}
                                  onClick={() => setGalleryIndex(idx)}
                                  className={`flex-shrink-0 rounded overflow-hidden border-2 ${
                                    idx === galleryIndex ? 'border-emerald-500' : 'border-transparent'
                                  }`}
                                  data-testid={`event-details-thumb-${idx}`}
                                >
                                  <img
                                    src={`${API_URL}/api/events/${detailEvent.id}/media/${img.id}`}
                                    alt=""
                                    className="w-16 h-16 object-cover"
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {vid && (
                        <video
                          src={`${API_URL}/api/events/${detailEvent.id}/media/${vid.id}`}
                          className="w-full max-h-96 bg-black rounded-lg"
                          controls
                          data-testid="event-details-video"
                        />
                      )}
                    </div>
                  );
                })()}

                {/* Description (HTML from rich text editor) */}
                {detailEvent.description && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">About this event</h4>
                    <div
                      className="prose prose-sm max-w-none text-slate-700"
                      data-testid="event-details-description"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detailEvent.description) }}
                    />
                  </div>
                )}

                {/* Facts grid */}
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CalendarDays className="w-4 h-4 mt-0.5 text-slate-400" />
                    <div>
                      <div className="font-medium text-slate-700">Date</div>
                      <div className="text-slate-600">{formatDate(detailEvent.start_datetime)}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 mt-0.5 text-slate-400" />
                    <div>
                      <div className="font-medium text-slate-700">Time</div>
                      <div className="text-slate-600">
                        {formatTime(detailEvent.start_datetime)} – {formatTime(detailEvent.end_datetime)}
                      </div>
                    </div>
                  </div>
                  {detailEvent.location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-700">Location</div>
                        <div className="text-slate-600">{detailEvent.location}</div>
                      </div>
                    </div>
                  )}
                  {detailEvent.is_virtual && detailEvent.virtual_meeting_link && (
                    <div className="flex items-start gap-2">
                      <LinkIcon className="w-4 h-4 mt-0.5 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-700">Meeting Link</div>
                        <a
                          href={detailEvent.virtual_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:underline break-all"
                          data-testid="event-details-meeting-link"
                        >
                          {detailEvent.virtual_meeting_link}
                        </a>
                      </div>
                    </div>
                  )}
                  {detailEvent.cost > 0 && (
                    <div className="flex items-start gap-2">
                      <DollarSign className="w-4 h-4 mt-0.5 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-700">Cost</div>
                        <div className="text-slate-600">
                          {cs()}{detailEvent.cost.toFixed(2)}
                          {detailEvent.cost_description && (
                            <span className="text-slate-400"> ({detailEvent.cost_description})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {detailEvent.dress_code && (
                    <div className="flex items-start gap-2">
                      <Shirt className="w-4 h-4 mt-0.5 text-slate-400" />
                      <div>
                        <div className="font-medium text-slate-700">Dress Code</div>
                        <div className="text-slate-600">{detailEvent.dress_code}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* What to bring */}
                {detailEvent.what_to_bring && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">What to Bring</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{detailEvent.what_to_bring}</p>
                  </div>
                )}

                {/* Capacity */}
                {detailEvent.max_capacity && (
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>Spots</span>
                      <span>{detailEvent.rsvp_counts?.total_attending || 0} / {detailEvent.max_capacity}</span>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          (detailEvent.rsvp_counts?.total_attending || 0) >= detailEvent.max_capacity
                            ? 'bg-red-500'
                            : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(100, ((detailEvent.rsvp_counts?.total_attending || 0) / detailEvent.max_capacity) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setDetailEvent(null)}>
                  Close
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" data-testid="event-details-add-to-calendar-btn">
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      Add to Calendar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => window.open(buildGoogleCalendarUrl(detailEvent), '_blank', 'noopener,noreferrer')}
                      data-testid="event-details-atc-google"
                    >
                      Google Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => downloadIcs(detailEvent)}
                      data-testid="event-details-atc-apple"
                    >
                      Apple Calendar (.ics)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => downloadIcs(detailEvent)}
                      data-testid="event-details-atc-outlook"
                    >
                      Outlook / Other (.ics)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {new Date(detailEvent.end_datetime) >= new Date() && !(detailEvent.rsvp_deadline && new Date(detailEvent.rsvp_deadline) < new Date()) && (
                  <Button
                    onClick={() => { const ev = detailEvent; setDetailEvent(null); openRsvpDialog(ev); }}
                    data-testid="event-details-rsvp-btn"
                  >
                    {detailEvent.user_rsvp ? 'Change RSVP' : 'RSVP Now'}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Event Card Component
const EventCard = ({ event, onRsvp, onOpenDetails, formatDate, formatTime, getStatusBadge, isPast }) => {
  const images = (event.media || []).filter(m => m.type === 'image');
  const video = (event.media || []).find(m => m.type === 'video');
  
  const isRsvpClosed = event.rsvp_deadline && new Date(event.rsvp_deadline) < new Date();
  const isFull = event.max_capacity && 
    (event.rsvp_counts?.total_attending || 0) >= event.max_capacity && 
    !event.enable_waitlist;

  // Stop propagation when clicking interactive children.
  const stop = (e) => e.stopPropagation();

  return (
    <Card
      className={`${isPast ? 'opacity-70' : ''} cursor-pointer transition-shadow hover:shadow-md`}
      onClick={onOpenDetails}
      data-testid={`event-card-${event.id}`}
    >
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row">
          {/* Media Section */}
          <div className="sm:w-1/3 bg-slate-100 relative">
            {images.length > 0 || video ? (
              <div className="aspect-video sm:aspect-square">
                {images.length > 0 ? (
                  <img
                    src={`${API_URL}/api/events/${event.id}/media/${images[0].id}`}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : video ? (
                  <video
                    src={`${API_URL}/api/events/${event.id}/media/${video.id}`}
                    className="w-full h-full object-cover"
                  />
                ) : null}
                
                {/* Media count badge */}
                {(images.length > 1 || video) && (
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    {images.length > 1 && (
                      <Badge className="bg-black/70">
                        <Image className="w-3 h-3 mr-1" />
                        {images.length}
                      </Badge>
                    )}
                    {video && (
                      <Badge className="bg-black/70">
                        <Video className="w-3 h-3" />
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video sm:aspect-square flex items-center justify-center">
                <CalendarDays className="w-12 h-12 text-slate-300" />
              </div>
            )}
          </div>
          
          {/* Content Section */}
          <div className="sm:w-2/3 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {event.user_rsvp && getStatusBadge(event.user_rsvp.status)}
                  {event.is_virtual && (
                    <Badge variant="outline"><LinkIcon className="w-3 h-3 mr-1" />Virtual</Badge>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{event.title}</h3>
              </div>
            </div>
            
            {event.description && (
              <p className="text-sm text-slate-500 mb-3">{eventDescriptionExcerpt(event.description)}</p>
            )}
            
            {/* Event Details */}
            <div className="space-y-2 text-sm mb-4">
              <div className="flex items-center gap-2 text-slate-600">
                <CalendarDays className="w-4 h-4 text-slate-400" />
                <span>{formatDate(event.start_datetime)}</span>
                <Clock className="w-4 h-4 text-slate-400 ml-2" />
                <span>{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
              </div>
              
              {event.location && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>{event.location}</span>
                </div>
              )}
              
              {event.cost > 0 && (
                <div className="flex items-center gap-2 text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span>{cs()}{event.cost.toFixed(2)}</span>
                  {event.cost_description && <span className="text-slate-400">({event.cost_description})</span>}
                </div>
              )}
              
              {event.dress_code && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Shirt className="w-4 h-4 text-slate-400" />
                  <span>{event.dress_code}</span>
                </div>
              )}
            </div>
            
            {/* Capacity */}
            {event.max_capacity && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Spots</span>
                  <span>{event.rsvp_counts?.total_attending || 0} / {event.max_capacity}</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${
                      (event.rsvp_counts?.total_attending || 0) >= event.max_capacity 
                        ? 'bg-red-500' 
                        : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, ((event.rsvp_counts?.total_attending || 0) / event.max_capacity) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* RSVP & Guest Counts */}
            <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
              <div className="p-2 bg-slate-50 rounded">
                <span className="text-slate-500 block mb-1">Responses</span>
                <div className="flex gap-2">
                  <span className="text-green-600">{event.rsvp_counts?.yes?.count || 0} Yes</span>
                  <span className="text-yellow-600">{event.rsvp_counts?.maybe?.count || 0} Maybe</span>
                </div>
              </div>
              <div className="p-2 bg-slate-50 rounded">
                <span className="text-slate-500 block mb-1">Attending</span>
                <span className="text-slate-700">
                  {(event.rsvp_counts?.yes?.count || 0) + (event.rsvp_counts?.yes?.guests || 0)} adults, {event.rsvp_counts?.yes?.children || 0} kids
                </span>
              </div>
            </div>
            
            {/* RSVP & Details Buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={(e) => { stop(e); onOpenDetails(); }}
                data-testid={`view-details-btn-${event.id}`}
              >
                View Details
              </Button>
              {!isPast && (
                <>
                  {isRsvpClosed ? (
                    <Badge variant="outline" className="text-slate-500">RSVP Closed</Badge>
                  ) : isFull && !event.user_rsvp ? (
                    <Badge variant="outline" className="text-red-500">Event Full</Badge>
                  ) : (
                    <Button
                      onClick={(e) => { stop(e); onRsvp(); }}
                      variant={event.user_rsvp ? 'outline' : 'default'}
                      data-testid={`rsvp-btn-${event.id}`}
                    >
                      {event.user_rsvp ? 'Change RSVP' : 'RSVP Now'}
                    </Button>
                  )}

                  {event.user_rsvp?.is_waitlisted && (
                    <Badge variant="outline" className="text-yellow-600">On Waitlist</Badge>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ParentEvents;
